/*
 * MAIN APPLICATION COMPONENT (SWA VERSION)
 * ========================================
 * This is the root component that sets up all the providers and routing.
 *
 * WHAT THIS FILE DOES:
 * 1. Sets up Azure Static Web Apps authentication (no MSAL)
 * 2. Provides authentication context to all child components
 * 3. Sets up global app state management
 * 4. Loads the main router that handles page navigation
 *
 * PROVIDER HIERARCHY (wrap order matters):
 * - AuthProvider: SWA authentication (cookie-based)
 * - AppProvider: Global app state (language, notifications, etc.)
 * - AppRouter: Page routing and navigation
 *
 * TO CUSTOMIZE:
 * - Authentication: Uses SWA built-in auth (/.auth/login/aad, /.auth/me)
 * - Global app behavior: Edit src/context/AppContext.tsx
 * - Page routing: Edit src/AppRouter.tsx
 */

import React from 'react';
import { Notifications } from '@mantine/notifications';
import { AppProvider } from '@/context/AppContext';
import { AuthProvider } from '@/components/Auth/AuthContext';
import AppShellBranding from '@/components/AppShellBranding';
import ErrorBoundary from '@/components/ErrorBoundary';
import AppRouter from '@/AppRouter';
import PwaInstallPrompt from '@/components/Pwa/PwaInstallPrompt';
import AppUpdatePrompt from '@/components/Pwa/AppUpdatePrompt';

const App = () => {
  return (
    // Global Error Boundary - catches all React errors
    <ErrorBoundary onError={(error, errorInfo) => {
      console.error('🚨 Global App Error:', error, errorInfo);
      // TODO: Send to logging service in production
    }}>
      <AppShellBranding />
      {/* Mantine notifications system */}
      <Notifications />
      <PwaInstallPrompt />
      <AppUpdatePrompt />
      {/* SWA Authentication provider - MUST wrap all components that use useAuth */}
      <AuthProvider>
        {/* Global app state provider - language, notifications, etc. */}
        <AppProvider>
          {/* Main router - handles which page to show */}
          <AppRouter />
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
